import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { SERVER_URL } from '../server_config';
import { appApi } from './app_api';

// After a "become" / "return", the identity behind the shared `jwt` cookie
// changes wholesale — reset both API caches so no user's data bleeds across the
// swap, then reload so the app re-bootstraps /users/me under the new identity.
const swapIdentity = async (dispatch, queryFulfilled) => {
  try {
    await queryFulfilled;
    dispatch(appApi.util.resetApiState());
    dispatch(adminApi.util.resetApiState());
    window.location.assign('/');
  } catch {
    // Failed — keep the current session/cache untouched.
  }
};

// The admin surface runs on its own RTK Query instance, separate from appApi, so
// its cache, tags and code-split bundle stay isolated from the main app. All
// endpoints are superadmin-only on the server (Admin::BaseController). One
// endpoint per model — added as each model's page is built.
export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: SERVER_URL,
    credentials: 'include',
  }),
  tagTypes: ['CustomerManagers', 'EventPlayers', 'QrCodes', 'Rounds', 'RoundInstances', 'Users', 'DummyUsers', 'InvitationConsents'],
  endpoints: (builder) => ({
    getEventStages: builder.query({
      query: () => '/admin/event_stages',
      transformResponse: (response) => response.data,
    }),
    getPlayerStatuses: builder.query({
      query: () => '/admin/player_statuses',
      transformResponse: (response) => response.data,
    }),
    getEventScoringPermissions: builder.query({
      query: () => '/admin/event_scoring_permissions',
      transformResponse: (response) => response.data,
    }),
    getEventTypes: builder.query({
      query: (params = {}) => ({ url: '/admin/event_types', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
    }),
    getQrCodes: builder.query({
      query: (params = {}) => ({ url: '/admin/qr_codes', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
      providesTags: ['QrCodes'],
    }),
    createQrCode: builder.mutation({
      query: (link) => ({
        url: '/admin/qr_codes',
        method: 'POST',
        body: { link },
      }),
      invalidatesTags: ['QrCodes'],
    }),
    updateQrCode: builder.mutation({
      query: ({ id, link }) => ({
        url: `/admin/qr_codes/${id}`,
        method: 'PATCH',
        body: { link },
      }),
      invalidatesTags: ['QrCodes'],
    }),
    getPointCategories: builder.query({
      query: () => '/admin/point_categories',
      transformResponse: (response) => response.data,
    }),
    getRounds: builder.query({
      query: (params = {}) => ({ url: '/admin/rounds', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
      providesTags: ['Rounds'],
    }),
    deleteRound: builder.mutation({
      query: (id) => ({ url: `/admin/rounds/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Rounds'],
    }),
    getRoundPlayers: builder.query({
      query: (roundId) => `/admin/rounds/${roundId}/players`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, roundId) => [{ type: 'RoundInstances', id: roundId }],
    }),
    replacePlayer: builder.mutation({
      query: ({ roundId, instanceId, playerId }) => ({
        url: `/admin/rounds/${roundId}/instances/${instanceId}/replace_player`,
        method: 'PATCH',
        body: { player_id: playerId },
      }),
      invalidatesTags: (result, error, { roundId }) => [{ type: 'RoundInstances', id: roundId }],
    }),
    getEvents: builder.query({
      query: (params = {}) => ({ url: '/admin/events', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
    }),
    getUsers: builder.query({
      query: (params = {}) => ({ url: '/admin/users', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
      providesTags: ['Users'],
    }),
    confirmUser: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/${userId}/confirm`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Users'],
    }),
    convertUserToDummy: builder.mutation({
      query: ({ userId, customerId }) => ({
        url: `/admin/users/${userId}/convert_to_dummy`,
        method: 'POST',
        body: { customer_id: customerId },
      }),
      // The user row disappears and a new dummy row appears.
      invalidatesTags: ['Users', 'DummyUsers'],
    }),
    getUserOptions: builder.query({
      query: (q) => ({ url: '/admin/users/options', params: { q } }),
      transformResponse: (response) => response.data,
    }),
    getDummyUsers: builder.query({
      query: (params = {}) => ({ url: '/admin/dummy_users', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
      providesTags: ['DummyUsers'],
    }),
    createDummyUser: builder.mutation({
      query: (body) => ({
        url: '/admin/dummy_users',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DummyUsers'],
    }),
    mergeDummyIntoUser: builder.mutation({
      query: ({ dummyId, userId }) => ({
        url: `/admin/dummy_users/${dummyId}/merge`,
        method: 'POST',
        body: { user_id: userId },
      }),
      // The dummy is destroyed and the target user's history changes.
      invalidatesTags: ['DummyUsers', 'Users'],
    }),
    getNewsletterSubscriptions: builder.query({
      query: (params = {}) => ({ url: '/admin/newsletter_subscriptions', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
    }),
    getCampaignLeads: builder.query({
      query: (params = {}) => ({ url: '/admin/campaign_leads', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
    }),
    getInvitationConsents: builder.query({
      query: (params = {}) => ({ url: '/admin/invitation_consents', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
      providesTags: ['InvitationConsents'],
    }),
    revokeInvitationConsent: builder.mutation({
      query: (id) => ({ url: `/admin/invitation_consents/${id}/revoke`, method: 'PATCH' }),
      invalidatesTags: ['InvitationConsents'],
    }),
    clearCache: builder.mutation({
      query: () => ({ url: '/admin/cache', method: 'DELETE' }),
    }),
    become: builder.mutation({
      query: (userId) => ({
        url: '/admin/impersonation',
        method: 'POST',
        body: { user_id: userId },
      }),
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        swapIdentity(dispatch, queryFulfilled);
      },
    }),
    stopImpersonation: builder.mutation({
      query: () => ({
        url: '/admin/impersonation',
        method: 'DELETE',
      }),
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        swapIdentity(dispatch, queryFulfilled);
      },
    }),
    getEventPlayers: builder.query({
      query: (eventId) => `/admin/events/${eventId}/players`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, eventId) => [{ type: 'EventPlayers', id: eventId }],
    }),
    renamePlayer: builder.mutation({
      query: ({ eventId, playerId, name }) => ({
        url: `/admin/events/${eventId}/players/${playerId}/rename`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    movePlayer: builder.mutation({
      query: ({ eventId, playerId, status }) => ({
        url: `/admin/events/${eventId}/players/${playerId}/move`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    deletePlayer: builder.mutation({
      query: ({ eventId, playerId }) => ({
        url: `/admin/events/${eventId}/players/${playerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    getCustomers: builder.query({
      query: (params = {}) => ({ url: '/admin/customers', params }),
      // Paginated envelope: returns the whole { data, meta } for useAdminList.
    }),
    getCustomerOptions: builder.query({
      query: (q) => ({ url: '/admin/customers/options', params: { q } }),
      transformResponse: (response) => response.data,
    }),
    getCustomerManagers: builder.query({
      query: (customerId) => `/admin/customers/${customerId}/managers`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, customerId) => [{ type: 'CustomerManagers', id: customerId }],
    }),
    removeManager: builder.mutation({
      query: ({ customerId, managerId }) => ({
        url: `/admin/customers/${customerId}/remove_manager`,
        method: 'DELETE',
        params: { manager_id: managerId },
      }),
      invalidatesTags: (result, error, { customerId }) => [{ type: 'CustomerManagers', id: customerId }],
    }),
    updateCustomer: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admin/customers/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    updateManagerPermission: builder.mutation({
      query: ({ customerId, managerId, permission, value }) => ({
        url: `/admin/customers/${customerId}/manager_permission`,
        method: 'PUT',
        body: { manager_id: managerId, permission, value },
      }),
    }),
  }),
});

export const {
  useClearCacheMutation,
  useGetEventStagesQuery,
  useGetPlayerStatusesQuery,
  useGetEventScoringPermissionsQuery,
  useGetEventTypesQuery,
  useGetQrCodesQuery,
  useCreateQrCodeMutation,
  useUpdateQrCodeMutation,
  useGetPointCategoriesQuery,
  useGetRoundsQuery,
  useDeleteRoundMutation,
  useGetRoundPlayersQuery,
  useReplacePlayerMutation,
  useGetEventsQuery,
  useGetUsersQuery,
  useConfirmUserMutation,
  useConvertUserToDummyMutation,
  useLazyGetUserOptionsQuery,
  useGetDummyUsersQuery,
  useCreateDummyUserMutation,
  useMergeDummyIntoUserMutation,
  useGetNewsletterSubscriptionsQuery,
  useGetCampaignLeadsQuery,
  useGetInvitationConsentsQuery,
  useRevokeInvitationConsentMutation,
  useBecomeMutation,
  useStopImpersonationMutation,
  useGetEventPlayersQuery,
  useRenamePlayerMutation,
  useMovePlayerMutation,
  useDeletePlayerMutation,
  useGetCustomersQuery,
  useLazyGetCustomerOptionsQuery,
  useGetCustomerManagersQuery,
  useUpdateManagerPermissionMutation,
  useUpdateCustomerMutation,
  useRemoveManagerMutation,
} = adminApi;
